import { highestInvoiceFirst } from "../../utils/invoiceSort";
import { apiErrorMessage } from "../../utils/apiError";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import SearchPending from "../../components/SearchPending";
import { useEffect, useState } from "react";
import { Modal, Popconfirm, message } from "antd";
import {
  Plus,
  Download,
  RefreshCw,
  MessageSquare,
  Eye,
  Trash2,
} from "lucide-react";
import PageHeading from "../../components/PageHeading";
import {
  loadFreelanceInvoices,
  freelanceInvoiceError,
  createFreelanceInvoice,
  getFreelanceInvoiceFile,
  setFreelanceInvoicePayment,
  deleteFreelanceInvoice,
} from "../../services/freelanceInvoiceApi";
import { contractDate } from "../../utils/planningDates";

const money = (value) =>
  value == null
    ? "—"
    : new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(value);
const date = (value) =>
  value
    ? new Date(
        /^\d{4}-\d{2}-\d{2}$/.test(value) ? value + "T12:00:00" : value,
      ).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "—";
export function invoicePayment(row, now = new Date()) {
  const due = contractDate(row.dateEcheance || row.DateEcheance);
  const today = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
  const dueDay = due
    ? `${due.getFullYear()}-${String(due.getMonth() + 1).padStart(2, "0")}-${String(due.getDate()).padStart(2, "0")}`
    : null;
  const overdue = dueDay != null && dueDay < today;
  const amountValue = Number(row.TotalTTC ?? row.montant ?? row.amount);
  const amount =
    Number.isFinite(amountValue) && amountValue > 0 ? amountValue : null;
  const explicit = row.paiementTotal ?? row.paidAmount ?? row.montantPaye;
  const paid =
    explicit != null &&
    Number.isFinite(Number(explicit)) &&
    Number(explicit) >= 0
      ? Number(explicit)
      : row.FactureRegle === "OUI"
        ? amount
        : null;
  const status =
    paid != null && amount != null && paid >= amount
      ? "Payée"
      : paid > 0 ||
          row.FactureRegle === "PARTIEL" ||
          row.FacturePartiellementReglee
        ? "Payée partiellement"
        : overdue
          ? "Impayée"
          : "En attente de paiement";
  return {
    amount,
    paid,
    remaining:
      paid != null && amount != null
        ? Math.max(0, Math.round((amount - paid) * 100) / 100)
        : null,
    status,
  };
}
const empty = { number: "", amount: "", issued: "", due: "", comment: "" };
const input = "mt-1 w-full rounded-lg border border-slate-200 p-2 text-sm";
export default function FreelanceInvoicesPage() {
  const [remote, setRemote] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [commentInvoice, setCommentInvoice] = useState(null);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    status: "outstanding",
    paid: "",
  });
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  useEffect(() => {
    setPreviewUrl("");
    if (!previewInvoice?._id) return;
    let active = true;
    let url = "";
    getFreelanceInvoiceFile(previewInvoice._id)
      .then((file) => {
        if (!active) return;
        url = URL.createObjectURL(file);
        setPreviewUrl(url);
      })
      .catch((error) => {
        if (active) message.error(apiErrorMessage(error, "Impossible d’ouvrir le document."));
      });
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [previewInvoice]);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [search, setSearch] = useState("");
  const [appliedSearch, searching] = useDebouncedValue(search);
  const [filter, setFilter] = useState("");
  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setRemote(await loadFreelanceInvoices());
    } catch (loadError) {
      setError(freelanceInvoiceError(loadError));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);
  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (
      !form.number.trim() ||
      !Number.isFinite(Number(form.amount)) ||
      Number(form.amount) <= 0 ||
      !form.issued ||
      !form.due ||
      form.due < form.issued ||
      !file ||
      file.type !== "application/pdf" ||
      file.size > 10 * 1024 * 1024
    ) {
      message.error(
        "Vérifiez les dates, le montant et le PDF (10 Mo maximum).",
      );
      return;
    }
    setSaving(true);
    try {
      const row = await createFreelanceInvoice({
        number: form.number.trim(),
        amount: Number(form.amount),
        issued: form.issued,
        due: form.due,
        comment: form.comment.trim(),
        file,
      });
      setRemote((current) => [row, ...current]);
      setOpen(false);
      message.success("Facture transmise à l’agence.");
    } catch (error) {
      message.error(apiErrorMessage(error, "Impossible d’envoyer la facture. Réessayez."));
    } finally {
      setSaving(false);
    }
  };
  const removeInvoice = async (row) => {
    if (row.paidAmount > 0 || deleting) return;
    setDeleting(row._id);
    try {
      await deleteFreelanceInvoice(row._id);
      setRemote((current) => current.filter((invoice) => invoice._id !== row._id));
      if (previewInvoice?._id === row._id) setPreviewInvoice(null);
      if (commentInvoice?._id === row._id) setCommentInvoice(null);
      message.success("Facture supprimée.");
    } catch (error) {
      message.error(apiErrorMessage(error, "Impossible de supprimer la facture."));
    } finally {
      setDeleting(null);
    }
  };
  const download = async (row) => {
    try {
      const file = await getFreelanceInvoiceFile(row._id);
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      message.error(apiErrorMessage(error, "Impossible de télécharger le document."));
    }
  };
  const openPayment = (row) => {
    setPaymentForm({
      status:
        row.payment.status === "Payée"
          ? "settled"
          : row.payment.status === "Payée partiellement"
            ? "partial"
            : "outstanding",
      paid: row.payment.paid ?? "",
    });
    setPaymentInvoice(row);
  };
  const savePayment = async (event) => {
    event.preventDefault();
    if (paymentSaving) return;
    const total = paymentInvoice.payment.amount;
    const paid =
      paymentForm.status === "settled"
        ? total
        : paymentForm.status === "outstanding"
          ? 0
          : Number(paymentForm.paid);
    if (
      total == null ||
      !Number.isFinite(paid) ||
      (paymentForm.status === "partial" && (paid <= 0 || paid >= total))
    ) {
      message.error(
        "Le montant partiel doit être supérieur à zéro et inférieur au total.",
      );
      return;
    }
    setPaymentSaving(true);
    try {
      const updated = await setFreelanceInvoicePayment(paymentInvoice._id, paid);
      setRemote((current) =>
        current.map((row) => (row._id === updated._id ? { ...row, ...updated } : row)),
      );
      setPaymentInvoice(null);
      message.success("Règlement mis à jour.");
    } catch (error) {
      message.error(apiErrorMessage(error, "Impossible d’enregistrer le règlement."));
    } finally {
      setPaymentSaving(false);
    }
  };
  const rows = [...remote]
    .sort(highestInvoiceFirst)
    .map((row) => ({ ...row, payment: invoicePayment(row) }))
    .filter(
      (row) =>
        (!filter ||
          (filter === "settled" && row.payment.status === "Payée") ||
          (filter === "outstanding" && row.payment.status !== "Payée") ||
          (filter === "partial" &&
            row.payment.status === "Payée partiellement")) &&
        [row.numeroFacture, row.agencyName, row.description]
          .join(" ")
          .toLocaleLowerCase("fr-FR")
          .includes(appliedSearch.toLocaleLowerCase("fr-FR")),
    );
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PageHeading
          icon="advances"
          title="Mes factures"
          description="Vos factures adressées à l’agence et le suivi de leurs règlements."
        />
        <button
          onClick={() => {
            setForm({ ...empty });
            setFile(null);
            setOpen(true);
          }}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white"
        >
          <Plus size={16} />
          Déposer ma facture
        </button>
      </div>
      <div className="flex flex-wrap gap-3">
        <input
          aria-label="Rechercher une facture"
          placeholder="Numéro, agence, commentaire…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border p-2 text-sm"
        />
        <SearchPending pending={searching || loading} label={loading ? "Chargement…" : "Recherche…"} />
        <select
          aria-label="Filtrer par état de paiement"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-lg border bg-white p-2 text-sm"
        >
          <option value="">Tous les états</option>
          <option value="settled">Soldée</option>
          <option value="outstanding">Non soldée</option>
          <option value="partial">Payée partiellement</option>
        </select>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          {loading ? "Actualisation…" : "Actualiser"}
        </button>
      </div>
      {error && (
        <p role="alert" className="text-sm text-rose-700">
          {error}
        </p>
      )}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-center text-sm">
          <thead>
            <tr>
              {[
                "Facture",
                "Émission",
                "Échéance",
                "Montant",
                "Versé",
                "Reste à payer",
                "Statut",
                "Commentaire",
                "Document",
              ].map((title) => (
                <th key={title} className="px-3 py-3 text-center">
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row._id} className="border-t border-slate-100">
                <td className="px-3 py-3 font-semibold">
                  {row.numeroFacture || row.IdFacture || "—"}
                </td>
                <td>{date(row.dateEmission || row.createdAt)}</td>
                <td>{date(row.dateEcheance || row.DateEcheance)}</td>
                <td>{money(row.payment.amount)}</td>
                <td>{money(row.payment.paid)}</td>
                <td>{money(row.payment.remaining)}</td>
                <td>
                  <button
                    type="button"
                    disabled
                    title="Règlement mis à jour par l’hyperadmin"
                    aria-label={`Statut du règlement de la facture ${row.numeroFacture || row.IdFacture}`}
                    className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${row.payment.status === "Payée" ? "bg-emerald-50 text-emerald-700" : row.payment.status === "Payée partiellement" ? "bg-amber-50 text-amber-700" : row.payment.status === "Impayée" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"}`}
                  >
                    {row.payment.status}
                  </button>
                </td>
                <td>
                  {row.description || row.comment ? (
                    <button
                      type="button"
                      onClick={() => setCommentInvoice(row)}
                      aria-label={`Voir le commentaire de la facture ${row.numeroFacture || row.IdFacture || ""}`}
                      title="Voir le commentaire"
                      className="rounded-lg p-2 text-blue-700 hover:bg-blue-50"
                    >
                      <MessageSquare size={18} />
                    </button>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  {row.fileName ? (
                    <div className="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        onClick={() => setPreviewInvoice(row)}
                        aria-label={`Visualiser la facture ${row.numeroFacture}`}
                        title="Visualiser"
                        className="rounded-lg p-2 text-blue-700 hover:bg-blue-50"
                      >
                        <Eye size={17} />
                      </button>
                      <button
                        type="button"
                        onClick={() => download(row)}
                        title="Télécharger"
                        aria-label={`Télécharger la facture ${row.numeroFacture}`}
                        className="rounded-lg p-2 text-blue-700 hover:bg-blue-50"
                      >
                        <Download size={17} />
                      </button>
                      {!(row.paidAmount > 0) && (
                        <Popconfirm
                          title={`Supprimer la facture ${row.numeroFacture} ?`}
                          description="La facture et son document seront supprimés pour vous et pour l’agence."
                          okText="Supprimer"
                          cancelText="Annuler"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => removeInvoice(row)}
                          disabled={Boolean(deleting)}
                        >
                          <button
                            type="button"
                            disabled={Boolean(deleting)}
                            title="Supprimer"
                            aria-label={`Supprimer la facture ${row.numeroFacture}`}
                            className="rounded-lg p-2 text-rose-600 hover:bg-rose-50 disabled:opacity-40"
                          >
                            <Trash2 size={17} />
                          </button>
                        </Popconfirm>
                      )}
                    </div>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={9} className="py-10 text-slate-500">
                  {loading
                    ? "Chargement…"
                    : "Aucune facture pour ces critères."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">
        Cliquez sur le statut pour renseigner le règlement reçu. Une facture
        déjà réglée, même partiellement, ne peut plus être supprimée.
      </p>
      <Modal
        open={Boolean(paymentInvoice)}
        centered
        footer={null}
        title={`Règlement — Facture ${paymentInvoice?.numeroFacture || paymentInvoice?.IdFacture || "—"}`}
        onCancel={() => !paymentSaving && setPaymentInvoice(null)}
        closable={!paymentSaving}
        maskClosable={!paymentSaving}
      >
        <form onSubmit={savePayment} className="space-y-4 pt-3">
          <p className="text-sm text-slate-500">
            Montant de la facture : {money(paymentInvoice?.payment.amount)}
          </p>
          <label className="block text-sm">
            Règlement
            <select
              value={paymentForm.status}
              onChange={(e) =>
                setPaymentForm({ ...paymentForm, status: e.target.value })
              }
              className={input}
            >
              <option value="outstanding">Non soldée</option>
              <option value="partial">Payée partiellement</option>
              <option value="settled">Soldée</option>
            </select>
          </label>
          {paymentForm.status === "partial" && (
            <label className="block text-sm">
              Montant total déjà versé (€)
              <input
                type="number"
                required
                min="0.01"
                step="0.01"
                value={paymentForm.paid}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, paid: e.target.value })
                }
                className={input}
              />
            </label>
          )}
          <p className="text-xs text-slate-500">
            Le reste à payer sera recalculé. Une facture non soldée devient
            impayée après son échéance.
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              disabled={paymentSaving}
              onClick={() => setPaymentInvoice(null)}
              className="rounded-lg border px-3 py-2"
            >
              Annuler
            </button>
            <button
              disabled={paymentSaving}
              className="rounded-lg bg-blue-700 px-3 py-2 text-white"
            >
              {paymentSaving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>
      </Modal>
      <Modal
        open={Boolean(previewInvoice)}
        centered
        width={960}
        footer={null}
        onCancel={() => setPreviewInvoice(null)}
        title={`Document — Facture ${previewInvoice?.numeroFacture || "—"}`}
      >
        {previewUrl && (
          <iframe
            src={previewUrl}
            title="Aperçu du document de la facture"
            className="mt-4 h-[70vh] w-full rounded-lg border border-slate-200"
          />
        )}
      </Modal>
      <Modal
        open={Boolean(commentInvoice)}
        centered
        footer={null}
        onCancel={() => setCommentInvoice(null)}
        title={`Commentaire — Facture ${commentInvoice?.numeroFacture || commentInvoice?.IdFacture || "—"}`}
      >
        <p className="mt-4 text-sm font-semibold text-slate-700">
          {commentInvoice?.commentAuthor ||
            commentInvoice?.commentator ||
            "Auteur non renseigné"}
        </p>
        {(commentInvoice?.commentCreatedAt || commentInvoice?.createdAt) && (
          <time className="mt-4 block text-xs text-slate-400">
            {new Date(
              commentInvoice.commentCreatedAt || commentInvoice.createdAt,
            ).toLocaleString("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit", year: "numeric" })}
          </time>
        )}
        <p className="mt-4 whitespace-pre-wrap break-words rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700">
          {commentInvoice?.description || commentInvoice?.comment}
        </p>
      </Modal>
      <Modal
        open={open}
        centered
        title="Déposer ma facture"
        footer={null}
        onCancel={() => !saving && setOpen(false)}
        closable={!saving}
        maskClosable={!saving}
      >
        {open && (
          <form onSubmit={save} className="space-y-4 pt-3">
            <p className="text-sm text-slate-500">
              Agence : {localStorage.getItem("agencyName") || "—"}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label>
                Numéro de facture
                <input
                  required
                  value={form.number}
                  onChange={(e) => setForm({ ...form, number: e.target.value })}
                  className={input}
                />
              </label>
              <label>
                Montant TTC (€)
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  required
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className={input}
                />
              </label>
              <label>
                Date d’émission
                <input lang="fr-FR"
                  type="date"
                  required
                  value={form.issued}
                  onChange={(e) => setForm({ ...form, issued: e.target.value })}
                  className={input}
                />
              </label>
              <label>
                Échéance
                <input lang="fr-FR"
                  type="date"
                  required
                  min={form.issued}
                  value={form.due}
                  onChange={(e) => setForm({ ...form, due: e.target.value })}
                  className={input}
                />
              </label>
            </div>
            <label className="block">
              Facture PDF
              <input
                type="file"
                accept=".pdf"
                required
                onChange={(e) => setFile(e.target.files[0] || null)}
                className={input}
              />
              <span className="text-xs text-slate-500">
                PDF · 10 Mo maximum
              </span>
            </label>
            <label className="block">
              Commentaire
              <textarea
                rows={2}
                maxLength={1000}
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                className={input}
              />
            </label>
            <p className="text-xs text-slate-500">
              La facture et son PDF sont transmis à l’agence dès l’enregistrement.
            </p>
            <div className="flex justify-end gap-3 border-t pt-4">
              <button
                type="button"
                disabled={saving}
                onClick={() => setOpen(false)}
                className="rounded-lg border px-3 py-2"
              >
                Annuler
              </button>
              <button
                disabled={saving}
                className="rounded-lg bg-blue-700 px-3 py-2 text-white"
              >
                {saving ? "Enregistrement…" : "Enregistrer le dépôt"}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
